
import { useState, useRef, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';

export const useAudioProcessing = (onAudioData: (data: string) => void) => {
  const { toast } = useToast();
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  const initializeAudioContext = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      audioStreamRef.current = stream;
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });

      // Create MediaRecorder with WebM format
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      // Handle audio data
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
          // Convert blob to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Audio = reader.result as string;
            // Remove the data URL prefix
            const base64Data = base64Audio.split(',')[1];
            onAudioData(base64Data);
          };
          reader.readAsDataURL(event.data);
        }
      };

      // Start recording
      chunks.current = [];
      mediaRecorderRef.current.start(1000); // Capture in 1-second intervals
    } catch (error) {
      console.error('Error initializing audio:', error);
      toast({
        title: "Error",
        description: "Failed to access microphone. Please ensure microphone permissions are granted.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return {
    initializeAudioContext,
    audioContextRef
  };
};
